import { Rect, G, SVG, Circle } from '@svgdotjs/svg.js';
import { gsap, Draggable } from 'gsap/all';
import { Subscription } from 'rxjs';
import Logger from '../../js/lib/logger';
import { InsertData, ModeType, SnapObject } from '../lib/types';
import { activeInsert$, boardMode$, message$, updateInsert$ } from '../lib/state';
import { HOTSPOT_RADIUS, Measurement } from '../lib/constants';
import Insert from './Insert';
import { getNearestInsertDataDown, getNearestInsertDataLeft, getNearestInsertDataRight, getNearestInsertDataUp } from '../lib/util.functions';

export default class RectangleTransformer {
  #NAME = 'RectangleTransformer';
  element: SVGElement;
  #snapObject: SnapObject;
  #group: G;
  #corners: {
    left: Rect,
    right: Rect,
    top: Rect,
    bottom: Rect,
    topLeft: Rect,
    topRight: Rect,
    bottomLeft: Rect,
    bottomRight: Rect,
  };
  #hotSpots: {
    top: Circle,
    bottom: Circle,
    left: Circle,
    right: Circle,
    topLeft: Circle,
    topRight: Circle,
    bottomLeft: Circle,
    bottomRight: Circle,
  };
  #draggables: Draggable[] = [];
  #show: boolean = false;
  get show(): boolean {
    return this.#show;
  }
  set show(b: boolean) {
    this.#show = b;
    this.element.style.display = this.#show ? 'initial' : 'none';
    if (!b) this.mode = 'nuetral';
  }
  #activeInsert: Insert | null = null;
  #sub?: Subscription;
  #mode: ModeType = 'nuetral';
  get mode(): ModeType {
    return this.#mode;
  }
  set mode(val: ModeType) {
    this.#mode = val;
    this.element.dataset.mode = this.#mode;
  }

  constructor(snapObject: { x: number[], y: number[] }) {
    Logger.info(`${this.#NAME} > constructor`);
    this.#snapObject = snapObject;
    this.#group = SVG().group().attr({ class: 'rect-transformer' });
    this.element = this.#group.node;
    this.element.style.display = 'none';
    //
    this.#corners = {
      left: this.#group.rect(8, 8),
      right: this.#group.rect(8, 8),
      top: this.#group.rect(8, 8),
      bottom: this.#group.rect(8, 8),
      topLeft: this.#group.rect(8, 8),
      topRight: this.#group.rect(8, 8),
      bottomLeft: this.#group.rect(8, 8),
      bottomRight: this.#group.rect(8, 8),
    };
    //
    this.#hotSpots = {
      top: this.#group.circle(HOTSPOT_RADIUS*2).attr({ 'data-hotspot': 'top' }),
      bottom: this.#group.circle(HOTSPOT_RADIUS*2).attr({ 'data-hotspot': 'bottom' }),
      left: this.#group.circle(HOTSPOT_RADIUS*2).attr({ 'data-hotspot': 'left' }),
      right: this.#group.circle(HOTSPOT_RADIUS*2).attr({ 'data-hotspot': 'right' }),
      topLeft: this.#group.circle(HOTSPOT_RADIUS*2).attr({ 'data-hotspot': 'top-left' }),
      topRight: this.#group.circle(HOTSPOT_RADIUS*2).attr({ 'data-hotspot': 'top-left' }),
      bottomLeft: this.#group.circle(HOTSPOT_RADIUS*2).attr({ 'data-hotspot': 'bottom-left' }),
      bottomRight: this.#group.circle(HOTSPOT_RADIUS*2).attr({ 'data-hotspot': 'bottom-right' }),
    };
    //
    activeInsert$.subscribe(insert => {
      this.#activeInsert = insert;
    });
  }

  remove() {
    this.#killDraggables();
    this.element.remove();
  }

  update(rect: { x: number, y: number, width: number, height: number }) {
    Logger.info(`${this.#NAME} > update >`, rect);
    this.#killDraggables();
    if (this.#activeInsert) {
      this.#updateElements(rect);
      this.#createDraggables();
    }
  }

  #updateElements(rect: { x: number, y: number, width: number, height: number }) {
    if ( rect.width < Measurement.MIN_INSERT_DIM_PX || rect.height < Measurement.MIN_INSERT_DIM_PX) return;
    this.#corners.left.attr({ x: rect.x - 4, y: rect.y + (rect.height / 2) - 4 });
    this.#corners.right.attr({ x: rect.x + rect.width - 4, y: rect.y + (rect.height / 2) - 4 });
    this.#corners.top.attr({ x: rect.x + (rect.width / 2) - 4, y: rect.y - 4 });
    this.#corners.bottom.attr({ x: rect.x + (rect.width / 2) - 4, y: rect.y + rect.height - 4 });
    this.#corners.topLeft.attr({ x: rect.x - 4, y: rect.y - 4 });
    this.#corners.topLeft.attr({ x: rect.x - 4, y: rect.y - 4 });
    this.#corners.topRight.attr({ x: rect.x + rect.width - 4, y: rect.y - 4 });
    this.#corners.bottomLeft.attr({ x: rect.x - 4, y: rect.y + rect.height - 4 });
    this.#corners.bottomRight.attr({ x: rect.x + rect.width - 4, y: rect.y + rect.height - 4 });
    //
    gsap.set(this.#hotSpots.top.node, { x: rect.x + (rect.width / 2) - HOTSPOT_RADIUS, y: rect.y - HOTSPOT_RADIUS });
    gsap.set(this.#hotSpots.bottom.node, { x: rect.x + (rect.width / 2) - HOTSPOT_RADIUS, y: rect.y + rect.height - HOTSPOT_RADIUS });
    gsap.set(this.#hotSpots.left.node, { x: rect.x - HOTSPOT_RADIUS, y: rect.y + (rect.height / 2) - HOTSPOT_RADIUS });
    gsap.set(this.#hotSpots.right.node, { x: rect.x + rect.width - HOTSPOT_RADIUS, y: rect.y + (rect.height / 2) - HOTSPOT_RADIUS });
    gsap.set(this.#hotSpots.topLeft.node, { x: rect.x - HOTSPOT_RADIUS, y: rect.y - HOTSPOT_RADIUS });
    gsap.set(this.#hotSpots.topRight.node, { x: rect.x + rect.width - HOTSPOT_RADIUS, y: rect.y - HOTSPOT_RADIUS });
    gsap.set(this.#hotSpots.bottomLeft.node, { x: rect.x - HOTSPOT_RADIUS, y: rect.y + rect.height - HOTSPOT_RADIUS });
    gsap.set(this.#hotSpots.bottomRight.node, { x: rect.x + rect.width - HOTSPOT_RADIUS, y: rect.y + rect.height - HOTSPOT_RADIUS });
  }

  #createDraggables() {
    this.#sub = updateInsert$.subscribe(dta => this.#updateElements({ x: dta.x, y: dta.y, width: dta.width, height: dta.height }));
    //
    let ewSnapXArr = [-HOTSPOT_RADIUS, ...this.#snapObject.x.slice(0, this.#snapObject.x.length - 1)];
    let nsSnapYArr = [-HOTSPOT_RADIUS, ...this.#snapObject.y.slice(0, this.#snapObject.y.length - 1)];
    if (this.#activeInsert) {
      const closestInsertDataLeft = getNearestInsertDataLeft(this.#activeInsert.data);
      const closestInsertDataRight = getNearestInsertDataRight(this.#activeInsert.data, this.#snapObject.x[this.#snapObject.x.length - 1]);
      ewSnapXArr = ewSnapXArr.filter(x => x >= (closestInsertDataLeft ? closestInsertDataLeft.x + closestInsertDataLeft.width - HOTSPOT_RADIUS : 0 ))
                            .filter(x => x <= (closestInsertDataRight ? closestInsertDataRight.x - HOTSPOT_RADIUS : this.#snapObject.x[this.#snapObject.x.length - 1]));
      const closestInsertDataUp = getNearestInsertDataUp(this.#activeInsert.data);
      const closestInsertDataDown = getNearestInsertDataDown(this.#activeInsert.data, this.#snapObject.y[this.#snapObject.y.length - 1]);
      nsSnapYArr = nsSnapYArr.filter(y => y >= (closestInsertDataUp ? closestInsertDataUp.y + closestInsertDataUp.height - HOTSPOT_RADIUS : 0 ))
                             .filter(y => y <= (closestInsertDataDown ? closestInsertDataDown.y - HOTSPOT_RADIUS : this.#snapObject.y[this.#snapObject.y.length - 1]));
    }
    /* console.log('ewSnapXArr', ewSnapXArr)
    console.log('nsSnapYArr', nsSnapYArr) */
    let data: InsertData;
    const ew = Draggable.create([this.#hotSpots.left.node, this.#hotSpots.right.node], {
      type: 'x',
      liveSnap: ewSnapXArr,
      cursor: 'ew-resize',
      onMove: function (insert: Insert) {
        if (this.target.dataset.hotspot === 'right') {
          data = { ...insert.data, width: this.x - insert.data.x + HOTSPOT_RADIUS };
        } else {
          data = { ...insert.data, x: this.x + HOTSPOT_RADIUS, width: insert.data.x - (this.x + HOTSPOT_RADIUS) + insert.data.width };
        }
        if (data.width >= Measurement.MIN_INSERT_DIM_PX && data.height >= Measurement.MIN_INSERT_DIM_PX ) {
          updateInsert$.next(data);
        }
      },
      onMoveParams: [this.#activeInsert],
      onPress: () => this.#onPress(),
      onRelease: () => this.#onRelease()
    });
    //
    const ns = Draggable.create([this.#hotSpots.top.node, this.#hotSpots.bottom.node], {
      type: 'y',
      liveSnap: nsSnapYArr,
      cursor: 'ns-resize',
      onMove: function (insert: Insert) {
        if (this.target.dataset.hotspot === 'bottom') {
          data = { ...insert.data, height: this.y - insert.data.y + HOTSPOT_RADIUS };
        } else {
          data = { ...insert.data, y: this.y + HOTSPOT_RADIUS, height: insert.data.y - (this.y + HOTSPOT_RADIUS) + insert.data.height }
        }
        if (data.width >= Measurement.MIN_INSERT_DIM_PX && data.height >= Measurement.MIN_INSERT_DIM_PX ) {
          updateInsert$.next(data);
        }
      },
      onMoveParams: [this.#activeInsert],
      onPress: () => this.#onPress(),
      onRelease: () => this.#onRelease()
    });
    //
    const nwse = Draggable.create([this.#hotSpots.topLeft.node, this.#hotSpots.bottomRight.node], {
      type: 'x,y',
      liveSnap: {
        x: ewSnapXArr,
        y: nsSnapYArr
      },
      cursor: 'nwse-resize',
      onMove: function (insert: Insert) {
        if (this.target.dataset.hotspot === 'bottom-right') {
          data = { 
            ...insert.data, 
            width: this.x - insert.data.x + HOTSPOT_RADIUS, 
            height: this.y - insert.data.y + HOTSPOT_RADIUS 
          };
        } else {
          data = { 
            ...insert.data, 
            x: this.x + HOTSPOT_RADIUS, 
            width: insert.data.x - (this.x + HOTSPOT_RADIUS) + insert.data.width, 
            y: this.y + HOTSPOT_RADIUS, 
            height: insert.data.y - (this.y + HOTSPOT_RADIUS) + insert.data.height
          }
        }
        if (data.width >= Measurement.MIN_INSERT_DIM_PX && data.height >= Measurement.MIN_INSERT_DIM_PX ) {
          updateInsert$.next(data);
        }
      },
      onMoveParams: [this.#activeInsert],
      onPress: () => this.#onPress(),
      onRelease: () => this.#onRelease()
    });
    //
    const nesw = Draggable.create([this.#hotSpots.topRight.node, this.#hotSpots.bottomLeft.node], {
      type: 'x,y',
      liveSnap: {
        x: ewSnapXArr,
        y: nsSnapYArr
      },
      cursor: 'nesw-resize',
      onMove: function (insert: Insert) {
        if (this.target.dataset.hotspot === 'bottom-left') {
          data = { 
            ...insert.data, 
            x: this.x + HOTSPOT_RADIUS, 
            width: insert.data.x - (this.x + HOTSPOT_RADIUS) + insert.data.width,
            height: this.y - insert.data.y + HOTSPOT_RADIUS
          };
        } else {
          data = { 
            ...insert.data, 
            width: this.x - insert.data.x + HOTSPOT_RADIUS,
            y: this.y + HOTSPOT_RADIUS, 
            height: insert.data.y - (this.y + HOTSPOT_RADIUS) + insert.data.height
          }
        }
        if (data.width >= Measurement.MIN_INSERT_DIM_PX && data.height >= Measurement.MIN_INSERT_DIM_PX ) {
          updateInsert$.next(data);
        }
      },
      onMoveParams: [this.#activeInsert],
      onPress: () => this.#onPress(),
      onRelease: () => this.#onRelease()
    });
    //
    this.#draggables = [ ...ew, ...ns, ...nwse, ...nesw ];
  }

  #onPress() {
    boardMode$.next('transform'); 
    message$.next({ show: false });
  }

  #onRelease() {
    boardMode$.next('nuetral');
  }

  #killDraggables() {
    this.#draggables.forEach(d => d.kill());
    this.#draggables = [];
    this.#sub?.unsubscribe();
  }
}